import React, { useEffect, useState } from 'react';
import { translate } from '~/training/sequence/templates/index';
import { Root } from '~/training/sequence/templates/items/root/Root';
import { MediaPlayerProvider } from '~/ui/video-player/MediaPlayerProvider';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import { SequenceProvider } from '~/training/sequence/SequenceStore';
import { Header } from '~/editor/header/Header';
import { Overview } from '~/editor/overview/Overview';
import { useEditorStore } from '~/shell/Store';

type EditorMode = 'editor' | 'preview';

interface MainContentProps {
  mode: string;
  setMode: (newMode: EditorMode) => void;
  exitRedirectUrl: string;
  showButtons: boolean;
  publishButtonTitle?: string | null;
  publishRedirectUrl?: string | null;
}

export const MainContent: React.FC<MainContentProps> = ({
  mode,
  setMode,
  exitRedirectUrl,
  showButtons,
  publishButtonTitle,
  publishRedirectUrl,
  ...restProps
}) => {
  const [content, setContent] = useState<null | JSX.Element>(null);
  const { sequence, forceUpdateSequence, setForceUpdateSequence } =
    useEditorStore();

  useEffect(() => {
    if (forceUpdateSequence) {
      if (mode === 'editor' && restProps) {
        setContent(renderItems(restProps));
      } else {
        if (restProps) {
          setContent(<Overview data={restProps} />);
        }
      }
      setForceUpdateSequence(false);
    }
  }, [mode, restProps, sequence, forceUpdateSequence]);

  function addTypename(obj: any): void {
    if (typeof obj !== 'object' || obj === null) return;

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        if (key === 'type' && typeof obj[key] === 'string') {
          const typeValue = obj[key];
          obj['__typename'] = typeValue
            .toLowerCase()
            .split('_')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
        }
        addTypename(obj[key]);
      }
    }
  }

  const renderItems = (el: any) => {
    addTypename(el);

    const RootComponent = new Root(translate(el)).component();

    return (
      <MediaPlayerProvider>
        <AudioPlayer />
        <SequenceProvider>
          <RootComponent key={`editor_${el.id}`} />
        </SequenceProvider>
      </MediaPlayerProvider>
    );
  };

  useEffect(() => {
    if (mode === 'editor' && restProps) {
      setContent(renderItems(restProps));
    } else {
      if (restProps) {
        setContent(<Overview data={restProps} />);
      }
    }
  }, [mode]);

  return (
    <div
      id="mainContentInTheCourseBuilder"
      style={{
        width: '100%',
        height: '100vh',
        overflow: 'auto'
      }}
    >
      {showButtons && (
        <Header
          mode={mode}
          setMode={(newMode: EditorMode) => setMode(newMode)}
          exitRedirectUrl={exitRedirectUrl}
          publishButtonTitle={publishButtonTitle}
          publishRedirectUrl={publishRedirectUrl}
        />
      )}
      {content}
    </div>
  );
};
